import { flexRender, type Row, type SortingState, type Table } from '@tanstack/react-table'
import clsx from 'clsx'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React from 'react'

import { IconButton, useBreakpoint } from 'slab'

import TableSkeleton from './Skeleton'
import { cn, handleTanstackSortingStateForSingleColumn } from '../../../utils'

export const DEFAULT_PAGE_SIZE = 20

export type PaginationMetadata = {
  total_records: number
  current_page: number
  total_pages: number
  next_page: number | null
  prev_page: number | null
}

const TanstackTableRow = <T,>({
  row,
  onRowClick,
  classes,
  clickable,
}: {
  row: Row<T>
  onRowClick?: ({
    event,
    row,
  }: {
    event: React.MouseEvent<HTMLTableRowElement>
    row: Row<T>
  }) => void
  classes?: ((row: Row<T>) => string | undefined) | string
  // boolean override to decide if the row should be clickable or not
  clickable?: boolean
}) => {
  const rowClasses = typeof classes === 'function' ? classes(row) : classes

  return (
    <tr
      key={row.id}
      onClick={(event) => onRowClick && onRowClick({ event, row })}
      className='group/row hover:bg-gray-50 transition'
    >
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          className={cn(
            'whitespace-nowrap py-2 px-3 first:pl-6 last:pr-6 body',
            rowClasses,
            onRowClick && clickable && 'cursor-pointer',
            onRowClick && clickable && 'group-hover/row:bg-gray-50'
          )}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  )
}

export function getHeaderCheckboxState<T> (table: Table<T>) {
  if (table.getIsAllPageRowsSelected()) return true
  if (table.getIsSomePageRowsSelected()) return 'indeterminate'
  return false
}

const TanstackTable = <T,>({
  table,
  isLoading = false,
  isError = false,
  errorMessage,
  noDataMessage,
  tableMessageHeightClass,
  onRowClick,
  rowClasses,
  tableClasses,
  headerRowClasses,
  skeletonRowClasses,
  isThisRowClicklable,
  setSorting,
  defaultSortingState,
  paginationLabelSingular,
  paginationLabelPlural = `${paginationLabelSingular}s`,
  paginationMetadata,
  onNextPageClick,
  onPreviousPageClick,
  hideTableOutlines = false,
  loadingSkeletonPageSizeOverride,
  showPageSelectionIfSinglePage = true,
  additionalPaginationContent,
}: {
  /* default table props */
  table: Table<T>
  isLoading?: boolean
  isError?: boolean
  errorMessage?: string
  noDataMessage?: React.ReactNode | string
  tableMessageHeightClass?: string
  /* table row related props */
  onRowClick?: ({
    event,
    row,
  }: {
    event: React.MouseEvent<HTMLTableRowElement>
    row: Row<T>
  }) => void
  rowClasses?: ((row: Row<T>) => string | undefined) | string
  tableClasses?: string
  headerRowClasses?: string
  skeletonRowClasses?: string
  isThisRowClicklable?: (row: Row<T>) => boolean
  /* sorting related props */
  setSorting?: React.Dispatch<React.SetStateAction<SortingState>>
  defaultSortingState?: SortingState
  /* pagination related props */
  paginationLabelSingular: string
  paginationLabelPlural?: string
  paginationMetadata?: PaginationMetadata // needed for server side pagination
  onNextPageClick?: () => void
  onPreviousPageClick?: () => void
  hideTableOutlines?: boolean
  loadingSkeletonPageSizeOverride?: number
  showPageSelectionIfSinglePage?: boolean
  additionalPaginationContent?: React.ReactNode
}) => {
  // Use default page handlers if the props are not provided.
  const handleNextPage = onNextPageClick || table.nextPage
  const handlePreviousPage = onPreviousPageClick || table.previousPage
  const totalCount = paginationMetadata
    ? paginationMetadata.total_records
    : table.getPrePaginationRowModel().rows.length
  const canNextPage = paginationMetadata
    ? paginationMetadata.next_page !== null
    : table.getCanNextPage()
  const canPreviousPage = paginationMetadata
    ? paginationMetadata.prev_page !== null
    : table.getCanPreviousPage()
  const totalPageCount = paginationMetadata
    ? paginationMetadata.total_pages
    : table.getPageCount()
  const currentPageIndex = paginationMetadata
    ? paginationMetadata.current_page
    : table.getState().pagination.pageIndex + 1
  const { breakpointIsAtLeast } = useBreakpoint()

  if (isLoading) {
    return (
      <TableSkeleton
        columnCount={table.getAllColumns().length}
        rowCount={
          loadingSkeletonPageSizeOverride ||
          table.getState().pagination.pageSize
        }
        rowClasses={skeletonRowClasses}
      />
    )
  }

  let tableMessage: React.ReactNode = null
  if (isError) {
    tableMessage = (
      <>
        There was an error loading this data{errorMessage ? `: ${errorMessage}` : ''}. Please reach out to{' '}
        <a href='mailto:support@thatch.com'>support@thatch.com</a> if you continue
        to experience this error.
      </>
    )
  } else if (noDataMessage && table.getRowModel().rows.length === 0) {
    tableMessage = noDataMessage
  }

  return (
    <div
      className={clsx({
        'bg-white rounded-2xl border border-gray-200 shadow-sm pt-2':
          !hideTableOutlines,
      })}
    >
      <div
        className={clsx('overflow-x-auto', {
          [`${tableClasses}`]: !!tableClasses,
        })}
      >
        <table className='min-w-full divide-y divide-gray-200'>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    scope='col'
                    className={`py-2 px-3 first:pl-6 last:pr-6 text-left body-sm text-gray-500 ${headerRowClasses}`}
                    onClick={() => {
                      if (header.column.columnDef.enableSorting && setSorting) {
                        handleTanstackSortingStateForSingleColumn(
                          header.id,
                          setSorting,
                          defaultSortingState
                        )
                      }
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className='divide-y divide-gray-200 bg-white'>
            {tableMessage && (
              <tr
                className={tableMessageHeightClass || 'h-96'}
                data-testid='TanstackTableMessage'
              >
                <td
                  colSpan={table.getAllColumns().length}
                  className='body text-center'
                >
                  {tableMessage}
                </td>
              </tr>
            )}
            {!tableMessage &&
              table
                .getRowModel()
                .rows.map((row) => (
                  <TanstackTableRow
                    key={row.id}
                    row={row}
                    onRowClick={onRowClick}
                    clickable={
                      isThisRowClicklable
                        ? isThisRowClicklable(row)
                        : !!onRowClick
                    }
                    classes={rowClasses}
                  />
                ))}
          </tbody>
        </table>
      </div>

      {/* ******************** Pagination Output ******************** */}

      {/* If there isn't more than one page of data, in some cases we don't want to show the pagination */}
      {(showPageSelectionIfSinglePage ||
        totalCount > table.getState().pagination.pageSize) && (
          <>
            <div className='flex items-center justify-between py-4 gap-2 px-6 border-t border-gray-200'>
              <div className='flex items-center gap-4'>
                {breakpointIsAtLeast('2xs')
                  ? (
                    <div className='body-sm-bold'>
                      {totalCount}{' '}
                      {totalCount === 1
                        ? paginationLabelSingular
                        : paginationLabelPlural}
                    </div>
                    )
                  : (
                    <div />
                    )}
                {additionalPaginationContent}
              </div>
              {totalCount > 0 && (
                <div className='flex gap-2 body'>
                  <IconButton
                    label='Previous page'
                    icon={ChevronLeftIcon}
                    onClick={() => handlePreviousPage()}
                    disabled={!canPreviousPage}
                  />
                  <span className='w-fit text-nowrap flex items-center gap-1 body-sm-bold'>
                    Page {currentPageIndex} of {totalPageCount}
                  </span>
                  <IconButton
                    label='Next page'
                    icon={ChevronRightIcon}
                    onClick={() => handleNextPage()}
                    disabled={!canNextPage}
                  />
                </div>
              )}
            </div>
            {!breakpointIsAtLeast('2xs') && (
              <div className='text-right body-sm-bold'>
                {'Total: '}
                {totalCount}{' '}
                {totalCount === 1
                  ? paginationLabelSingular
                  : paginationLabelPlural}
              </div>
            )}
          </>
      )}
    </div>
  )
}

export default TanstackTable
